import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Bell, Megaphone, Wrench, Lightbulb, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UPDATE_TYPES = {
  announcement: { label: 'Announcement', icon: Megaphone, color: 'bg-blue-100 text-blue-800' },
  system_update: { label: 'System Update', icon: Wrench, color: 'bg-gray-100 text-gray-800' },
  exam_tips: { label: 'Exam Tips', icon: Lightbulb, color: 'bg-yellow-100 text-yellow-800' },
  new_feature: { label: 'New Feature', icon: Sparkles, color: 'bg-purple-100 text-purple-800' }
};

const PRIORITY_STYLES = {
  normal: 'border-l-gray-300',
  important: 'border-l-yellow-400',
  urgent: 'border-l-red-500'
};

// System notification types (payments, 2FA, live classes, host events, etc.)
// get a friendly default badge label.
const SYSTEM_TYPE_LABELS = {
  live_class_rescheduled: 'Class rescheduled',
  live_class_cancelled: 'Class cancelled',
  live_class_host_assigned: 'Host assigned',
  live_class_archive_published: 'Class recap',
  class_reminder_1h: 'Reminder',
  class_starting_15m: 'Starting soon',
  class_live_now: 'Live now',
};

const UpdatesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [systemNotifs, setSystemNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [updatesTotal, setUpdatesTotal] = useState(0);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const [updRes, sysRes] = await Promise.all([
        axios.get(`${API}/updates/my-updates?page=${page}&per_page=20`).catch(() => ({ data: { updates: [], total: 0, page: 1, per_page: 20 } })),
        // Only fetch system notifications on page 1 — they're not paginated
        // in the current backend endpoint (returns latest 100).
        page === 1
          ? axios.get(`${API}/notifications?limit=100`).catch(() => ({ data: { notifications: [] } }))
          : Promise.resolve({ data: { notifications: [] } }),
      ]);

      const nextUpdates = updRes.data.updates || [];
      if (page === 1) {
        setUpdates(nextUpdates);
        setSystemNotifs(sysRes.data.notifications || []);
      } else {
        setUpdates((prev) => [...prev, ...nextUpdates]);
      }
      setUpdatesTotal(updRes.data.total || 0);
      setHasMore(((updRes.data.page || 1) * (updRes.data.per_page || 20)) < (updRes.data.total || 0));
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    } finally {
      setLoading(false);
    }
  }, [user, page]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const getTypeConfig = (type) => UPDATE_TYPES[type] || UPDATE_TYPES.announcement;

  // Normalise a system notification's `link` into a same-origin route or an
  // external URL. Returns null if no link.
  const openLink = (link) => {
    if (!link) return;
    try {
      const u = new URL(link, window.location.origin);
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search + u.hash);
      } else {
        window.location.href = link;
      }
    } catch (_) {
      // Ignore malformed URLs
    }
  };

  const markSystemRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/mark-read`);
      setSystemNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (_) { /* non-blocking */ }
  };

  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
              <p className="text-gray-600">You need to be logged in to view updates.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const totalCount = updatesTotal + systemNotifs.length;
  const nothing = !loading && updates.length === 0 && systemNotifs.length === 0;

  return (
    <Layout>
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-gray-600" data-testid="updates-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-8 h-8 text-[#0A7EA4]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Updates</h1>
          <p className="text-gray-600">{totalCount} update{totalCount !== 1 ? 's' : ''} for you</p>
        </div>
      </div>

      {loading && page === 1 ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0A7EA4]" />
          <p className="text-gray-500 mt-2">Loading updates...</p>
        </div>
      ) : nothing ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold mb-2">No Updates Yet</h2>
            <p className="text-gray-600">You will see important announcements and updates here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* System notifications (payments, 2FA, live classes, etc.) — shown
              above admin-authored updates because they're user-scoped and
              usually more actionable. */}
          {systemNotifs.length > 0 && (
            <div className="space-y-3" data-testid="updates-system-section">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your notifications</p>
              {systemNotifs.map((n) => {
                const label = SYSTEM_TYPE_LABELS[n.type] || 'Notification';
                const clickable = !!n.link;
                return (
                  <Card
                    key={n.id}
                    className={`border-l-4 ${n.is_read ? 'border-l-gray-200' : 'border-l-[#0A7EA4]'} ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                    onClick={() => {
                      if (!n.is_read) markSystemRead(n.id);
                      openLink(n.link);
                    }}
                    data-testid={`updates-sys-notif-${n.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-[#0A7EA4]/10">
                          <Bell className="w-5 h-5 text-[#0A7EA4]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-[#0A7EA4]/10 text-[#0A7EA4] hover:bg-[#0A7EA4]/10">{label}</Badge>
                            {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#0A7EA4]" />}
                          </div>
                          <h3 className={`text-gray-900 mb-1 ${n.is_read ? 'font-medium' : 'font-semibold'}`}>{n.title}</h3>
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(n.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Admin-authored broadcast updates */}
          {updates.length > 0 && (
            <>
              {systemNotifs.length > 0 && (
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Announcements</p>
              )}
              {updates.map(update => {
                const typeConfig = getTypeConfig(update.update_type);
                const TypeIcon = typeConfig.icon;
                const priorityStyle = PRIORITY_STYLES[update.priority] || PRIORITY_STYLES.normal;

                return (
                  <Card key={update.id} className={`border-l-4 ${priorityStyle}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                            {update.priority === 'urgent' && (
                              <Badge className="bg-red-100 text-red-800">Urgent</Badge>
                            )}
                            {update.priority === 'important' && (
                              <Badge className="bg-yellow-100 text-yellow-800">Important</Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{update.title}</h3>
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">{update.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(update.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}

          {/* Load More (admin updates only — system notifs aren't paginated) */}
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={loading}
                data-testid="updates-load-more-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
    </Layout>
  );
};

export default UpdatesPage;
